import { motion } from "framer-motion";
import { MapPin, Phone, Clock, ArrowUpRight } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { useQuote } from "@/context/QuoteContext";
import { COMPANY } from "@/i18n/translations";

export default function Contact() {
  const { t } = useI18n();
  const { openQuote } = useQuote();
  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(COMPANY.mapQuery)}&output=embed`;

  const rows = [
    { icon: MapPin, label: t("contact.address"), value: COMPANY.address, href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(COMPANY.mapQuery)}` },
    { icon: Phone, label: t("contact.phone"), value: COMPANY.phone, href: `tel:${COMPANY.phoneIntl}` },
    { icon: Clock, label: t("contact.hours"), value: t("contact.hoursValue") },
  ];

  return (
    <section id="contact" className="mx-auto max-w-[1400px] scroll-mt-24 px-5 py-24 md:px-10 md:py-32">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
        <div>
          <span className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-primary">
            <span className="h-px w-8 bg-primary" /> {t("contact.title")}
          </span>
          <h2 className="font-display text-4xl font-bold uppercase leading-none tracking-tight md:text-6xl">
            {t("contact.subtitle")}
          </h2>

          <div className="mt-10 space-y-px border-t border-border">
            {rows.map((r, i) => {
              const Inner = (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex items-start gap-4 border-b border-border py-5"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center bg-secondary">
                    <r.icon className="h-5 w-5 text-primary" />
                  </span>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{r.label}</p>
                    <p className="mt-1 font-medium leading-relaxed">{r.value}</p>
                  </div>
                </motion.div>
              );
              return r.href ? (
                <a key={i} href={r.href} target="_blank" rel="noopener noreferrer"
                  data-testid={`contact-${r.label}`} className="block transition-colors hover:text-primary">
                  {Inner}
                </a>
              ) : (
                <div key={i} data-testid={`contact-${r.label}`}>{Inner}</div>
              );
            })}
          </div>

          <button
            data-testid="contact-quote-btn"
            onClick={() => openQuote(null)}
            className="mt-8 flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/20 transition-[transform,box-shadow] duration-300 hover:scale-105 hover:shadow-xl hover:shadow-primary/30"
          >
            {t("contact.cta")} <ArrowUpRight className="h-4 w-4" />
          </button>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative min-h-[380px] overflow-hidden border border-border bg-secondary lg:min-h-full"
        >
          <iframe
            title="map"
            src={mapSrc}
            className="h-full min-h-[380px] w-full grayscale contrast-125"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </motion.div>
      </div>
    </section>
  );
}
